import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const Privacy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | VCapz Media";
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "June 2025";

  return (
    <div className="min-h-screen bg-background pt-28 pb-20">
      <article className="w-full max-w-3xl mx-auto px-4 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors mb-10">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <Shield size={28} className="text-primary" />
          <h1 className="text-4xl md:text-5xl font-black text-white">Privacy Policy</h1>
        </div>
        <p className="text-text-muted mb-12">Last updated: {lastUpdated}</p>

        <div className="prose-blog space-y-10">

          <section>
            <h2>1. Introduction</h2>
            <p>
              VCapz Media ("we", "our", or "us") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and safeguard information when you visit <strong>vcapz.com</strong> or engage with our services including content creation, social media management, and web development.
            </p>
            <p>By using our website, you agree to the terms outlined in this policy.</p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <h3>Information you provide directly</h3>
            <ul>
              <li>Name, email address, and phone number submitted via our contact or enquiry form</li>
              <li>Any project details, briefs, or messages you send to us</li>
            </ul>
            <h3>Information collected automatically</h3>
            <ul>
              <li>IP address and general geographic location</li>
              <li>Browser type, device type, and operating system</li>
              <li>Pages visited, time spent, and referral sources</li>
              <li>Cookies and usage analytics (see Section 5)</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Respond to enquiries and provide quotes or proposals</li>
              <li>Deliver and manage services you have engaged us for</li>
              <li>Improve our website experience and content</li>
              <li>Send relevant updates or communications (with your consent)</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>We do not sell, trade, or rent your personal information to third parties.</p>
          </section>

          <section>
            <h2>4. Legal Basis for Processing</h2>
            <p>We process your data under the following legal bases:</p>
            <ul>
              <li><strong>Consent</strong> — when you submit a form or opt into communications</li>
              <li><strong>Legitimate interest</strong> — to maintain and improve our services</li>
              <li><strong>Contractual necessity</strong> — to fulfil service agreements with clients</li>
              <li><strong>Legal obligation</strong> — where required by applicable law</li>
            </ul>
          </section>

          <section>
            <h2>5. Cookies and Analytics</h2>
            <p>
              Our website may use cookies and third-party analytics tools including <strong>Google Analytics</strong> and <strong>Microsoft Clarity</strong> to understand how visitors interact with our site. These tools collect anonymised usage data.
            </p>
            <p>
              You can disable cookies through your browser settings at any time. Disabling cookies will not affect your ability to view the site.
            </p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>
              We retain personal data for as long as necessary to fulfil the purposes outlined in this policy or as required by law. Enquiry data is typically retained for 12 months. Client project data is retained for the duration of the engagement and for a reasonable period thereafter.
            </p>
          </section>

          <section>
            <h2>7. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent at any time</li>
              <li>Lodge a complaint with your local data protection authority</li>
            </ul>
            <p>To exercise any of these rights, contact us at <a href="mailto:mail@vcapz.com">mail@vcapz.com</a>.</p>
          </section>

          <section>
            <h2>8. Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites and encourage you to review their privacy policies independently.
            </p>
          </section>

          <section>
            <h2>9. Children's Privacy</h2>
            <p>
              Our services are not directed to individuals under the age of 16. We do not knowingly collect personal data from children. If you believe we have inadvertently collected such data, please contact us immediately.
            </p>
          </section>

          <section>
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. Continued use of the site after changes constitutes acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2>11. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact:</p>
            <p>
              <strong>VCapz Media</strong><br />
              Email: <a href="mailto:mail@vcapz.com">mail@vcapz.com</a><br />
              Website: <a href="https://vcapz.com">vcapz.com</a>
            </p>
          </section>

        </div>

        <div className="mt-16 pt-10 border-t border-white/10">
          <Link to="/terms" className="text-primary hover:underline text-sm font-semibold">
            Read our Terms of Service →
          </Link>
        </div>
      </article>
    </div>
  );
};

export default Privacy;
